/**
 * API Endpoints
 * Centralized endpoint constants
 */

export const API_ENDPOINTS = {
    // Auth
    AUTH: {
        LOGIN: '/login',
        // REGISTER: '/register',
        LOGOUT: '/logout',
        REFRESH: '/refresh',
        ME: '/me',
        RESET_PASSWORD: '/reset-password',
        FORGOT_PASSWORD: '/forgot-password',
    },

    CONTACT: '/contact',

    // Users
    USERS: {
        LIST: '/users',
        DETAIL: (id: string) => `/users/${id}`,
        CREATE: '/users',
        UPDATE: (id: string) => `/users/${id}`,
        DELETE: (id: string) => `/users/${id}`,
    },

    // Schools (Tenants)
    SCHOOLS: {
        LIST: '/schools',
        CREATE: '/schools',
        DETAIL: (id: string) => `/schools/${id}`,
        UPDATE: (id: string) => `/schools/${id}`,
        DELETE: (id: string) => `/schools/${id}`,
        TOGGLE_STATUS: (id: string) => `/schools/${id}/toggle-status`,
        PUBLIC: (slug: string) => `/schools/public/${slug}`,
        USERS: (slug: string) => `/schools/${slug}/users`,
    },


    // Departments
    DEPARTMENTS: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/departments`,
        CREATE: (schoolSlug: string) => `/${schoolSlug}/departments`,
        UPDATE: (schoolSlug: string, departmentId: string) => `/${schoolSlug}/departments/${departmentId}`,
        LEVELS: (schoolSlug: string, departmentId: string) => `/${schoolSlug}/departments/${departmentId}/levels`,
        DELETE: (schoolSlug: string, departmentId: string) => `/${schoolSlug}/departments/${departmentId}`,
    },

    // Import/Export
    IMPORT_EXPORT: {
        DOWNLOAD_TEMPLATE: (schoolSlug: string, type: string) => `/${schoolSlug}/import-export/template/${type}`,
        IMPORT_FILE: (schoolSlug: string, type: string) => `/${schoolSlug}/import-export/import/${type}`,
    },

    // Sub-Departments
    SUB_DEPARTMENTS: {
        CREATE: (schoolSlug: string) => `/${schoolSlug}/department-types`,
        LIST: (schoolSlug: string, departmentId: string) => `/${schoolSlug}/departments/${departmentId}/types`,
        UPDATE: (schoolSlug: string, typeId: string) => `/${schoolSlug}/department-types/${typeId}`,
        DELETE: (schoolSlug: string, typeId: string) => `/${schoolSlug}/department-types/${typeId}`,
    },

    // Employees
    EMPLOYEES: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/employees`,
        INVITE: (schoolSlug: string) => `/${schoolSlug}/employees/invite`,
        UPDATE: (schoolSlug: string, employeeId: string) => `/${schoolSlug}/employees/${employeeId}`,
        DELETE: (schoolSlug: string, employeeId: string) => `/${schoolSlug}/employees/${employeeId}`,
    },

    // Parents
    PARENTS: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/student-parents/parents`,
        INVITE: (schoolSlug: string) => `/${schoolSlug}/student-parents/invite`,
        UPDATE: (schoolSlug: string, userId: string) => `/${schoolSlug}/student-parents/parents/${userId}`,
        DELETE: (schoolSlug: string, userId: string) => `/${schoolSlug}/student-parents/parents/${userId}`,
        ASSIGNMENT: (schoolSlug: string) => `/${schoolSlug}/student-parents/assignment`,
    },

    // Students
    STUDENTS: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/student-parents/students`,
        CREATE: (schoolSlug: string) => `/${schoolSlug}/student-parents/students`,
        UPDATE: (schoolSlug: string, studentId: string) => `/${schoolSlug}/student-parents/students/${studentId}`,
        DELETE: (schoolSlug: string, studentId: string) => `/${schoolSlug}/student-parents/students/${studentId}`,
        SEARCH: (schoolSlug: string) => `/${schoolSlug}/student-parents/students/search`,
    },


    // Workflow
    // WORKFLOW_STEPS: {
    //     CREATE: (schoolSlug: string) => `/${schoolSlug}/workflow-steps`,
    //     LIST: (schoolSlug: string, typeId: string) => `/${schoolSlug}/department-types/${typeId}/steps`,
    //     DELETE: (schoolSlug: string, stepId: string) => `/${schoolSlug}/workflow-steps/${stepId}`,
    // },
    // WORKFLOW_LEVELS: {
    //     CREATE: (schoolSlug: string) => `/${schoolSlug}/workflow-levels`,
    //     DELETE: (schoolSlug: string, levelId: string) => `/${schoolSlug}/workflow-levels/${levelId}`,
    // },

    // New Workflows
    WORKFLOWS_NEW: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/workflows-new/`,
        CREATE: (schoolSlug: string) => `/${schoolSlug}/workflows-new/`,
        BY_DEPARTMENT: (schoolSlug: string, departmentId: string) => `/${schoolSlug}/workflows-new/department/${departmentId}`,
        UPDATE: (schoolSlug: string, workflowId: string) => `/${schoolSlug}/workflows-new/${workflowId}`,
        DELETE: (schoolSlug: string, workflowId: string) => `/${schoolSlug}/workflows-new/${workflowId}`,
        STEPS: {
            CREATE: (schoolSlug: string) => `/${schoolSlug}/workflows-new/steps`,
            LIST: (schoolSlug: string, workflowId: string) => `/${schoolSlug}/workflows-new/${workflowId}/steps`,
            UPDATE: (schoolSlug: string, stepId: string) => `/${schoolSlug}/workflows-new/steps/${stepId}`,
            DELETE: (schoolSlug: string, stepId: string) => `/${schoolSlug}/workflows-new/steps/${stepId}`,
        },
        LEVELS: {
            CREATE: (schoolSlug: string) => `/${schoolSlug}/workflows-new/levels`,
            UPDATE: (schoolSlug: string, levelId: string) => `/${schoolSlug}/workflows-new/levels/${levelId}`,
            DELETE: (schoolSlug: string, levelId: string) => `/${schoolSlug}/workflows-new/levels/${levelId}`,
        }
    },

    // Tags
    TAGS: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/tags/`,
        CREATE: (schoolSlug: string) => `/${schoolSlug}/tags/`,
        UPDATE: (schoolSlug: string, tagId: string) => `/${schoolSlug}/tags/${tagId}`,
        DELETE: (schoolSlug: string, tagId: string) => `/${schoolSlug}/tags/${tagId}`,
    },

    // Classes
    CLASSES: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/classes/`,
        CREATE: (schoolSlug: string) => `/${schoolSlug}/classes/`,
        UPDATE: (schoolSlug: string, classId: string) => `/${schoolSlug}/classes/${classId}`,
        DELETE: (schoolSlug: string, classId: string) => `/${schoolSlug}/classes/${classId}`,
    },

    // Tickets
    TICKETS: {
        ADMIN_LIST: (schoolSlug: string) => `/${schoolSlug}/admin/tickets`,
        DETAIL: (schoolSlug: string, ticketId: string) => `/${schoolSlug}/tickets/${ticketId}`,
        UPDATE_STATUS: (schoolSlug: string, ticketId: string) => `/${schoolSlug}/tickets/${ticketId}/status`,
        HISTORY: (schoolSlug: string, ticketId: string) => `/${schoolSlug}/tickets/${ticketId}/history`,
        CHANGE_WORKFLOW: (schoolSlug: string, ticketId: string) => `/${schoolSlug}/tickets/${ticketId}/change-workflow`,
    },

    // School Settings
    SCHOOL_SETTINGS: {
        UPDATE: (schoolSlug: string) => `/upload/${schoolSlug}/settings/`,
        EMAIL_CONFIG: (schoolSlug: string) => `/${schoolSlug}/email-config/`,
    },

    // Knowledge Hub
    KNOWLEDGE_HUB: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/kb/`,
        UPLOAD: (schoolSlug: string) => `/${schoolSlug}/kb/upload`,
        DELETE: (schoolSlug: string, kbId: string) => `/${schoolSlug}/kb/${kbId}`,
    },

    // Dashboard
    DASHBOARD: {
        SUPER_ADMIN: '/dashboard',
        SCHOOL: (schoolSlug: string) => `/${schoolSlug}/dashboard`,
    },

    // Notifications
    NOTIFICATIONS: {
        LIST: (schoolSlug: string) => `/${schoolSlug}/notifications/`,
        MARK_READ: (schoolSlug: string, notificationId: string) => `/${schoolSlug}/notifications/${notificationId}/read`,
        GLOBAL_LIST: '/global-notifications',
        GLOBAL_MARK_READ: (notificationId: string) => `/global-notifications/${notificationId}/read`,
    },

    // Add more endpoints as needed
} as const;
